/**
 * analytics.ts — wrapper alrededor de posthog-js.
 *
 * Punto único para capturar eventos de producto. Si las env vars no están
 * seteadas (VITE_POSTHOG_API_KEY ausente), todo es no-op silencioso. Esto
 * permite levantar el proyecto en local sin necesidad de credenciales.
 *
 * USO:
 *   import { track, identify, resetAnalytics } from '@/lib/analytics';
 *   track('booking_payment_succeeded', { booking_id, amount_cents });
 *
 * PRIVACIDAD: NUNCA enviamos:
 *   - email, full_name, handle (PII)
 *   - contenido de posts/comments/messages
 *   - números de cuenta, tarjetas, montos exactos en context personal
 * Solo IDs (user_id, booking_id, guru_id) + metadata categórica.
 *
 * Los eventos están alineados a los KPIs leading del PRD sección 6.3.
 */
import posthog from 'posthog-js';
import { metaDesdeProducto } from '@/lib/meta-pixel';

const API_KEY = import.meta.env.VITE_POSTHOG_API_KEY;
const HOST = import.meta.env.VITE_POSTHOG_HOST ?? 'https://us.i.posthog.com';

let initialized = false;

/**
 * Inicializa PostHog. Idempotente. Si no hay API key, no hace nada y los
 * track/identify subsiguientes son no-op.
 */
export function initAnalytics(): void {
  if (initialized) return;
  if (!API_KEY) {
    if (import.meta.env.DEV) {
      console.info('[analytics] VITE_POSTHOG_API_KEY no configurada — eventos deshabilitados');
    }
    return;
  }
  try {
    posthog.init(API_KEY, {
      api_host: HOST,
      person_profiles: 'identified_only', // crea person solo cuando hacemos identify
      capture_pageview: true,             // pageviews automaticos en navegación
      capture_pageleave: true,
      autocapture: false,                 // solo eventos custom — más control y menos ruido
      // Session replay OFF: comprime con un blob/worker que Safari bloquea
      // ("access control checks" en gzip.mjs) y rompe el render en iOS Safari.
      // Además no aporta en la web pública. Esto elimina el flood de /s/.
      disable_session_recording: true,
      // localStorage+cookie: si Safari bloquea localStorage (Bloquear cookies),
      // PostHog cae a cookie en vez de lanzar. Defensa extra al try/catch.
      persistence: 'localStorage+cookie',
      loaded: (ph) => {
        if (import.meta.env.DEV) {
          ph.debug();
        }
      },
    });
    initialized = true;
  } catch (e) {
    // Safari estricto / bloqueadores: no rompas la app por analytics.
    console.error('[analytics] posthog.init falló, analytics deshabilitado:', e);
  }
}

/**
 * Identifica al usuario actual con sus propiedades para segmentación.
 * Llamar al login y cuando cambien propiedades clave (plan, rol).
 */
export function identify(
  userId: string,
  props?: {
    role?: 'user' | 'guru' | 'admin' | null;
    plan_code?: string | null;
    country_code?: string | null;
    signed_up_at?: string | null;
  },
): void {
  if (!API_KEY || !initialized) return;
  posthog.identify(userId, props ?? undefined);
}

/**
 * Captura un evento custom. Las props deben ser categóricas / IDs — nunca
 * datos personales identificables.
 */
export function track(event: AnalyticsEvent, props?: Record<string, unknown>): void {
  // Píxel de Meta: va aparte de PostHog (funciona aunque PostHog no esté configurado).
  metaDesdeProducto(event, props);
  if (!API_KEY || !initialized) return;
  posthog.capture(event, props);
}

/**
 * Limpia identidad y cookies. Llamar al logout para que la siguiente
 * sesión arranque como usuario anónimo distinto.
 */
/**
 * Estampa en TODOS los eventos qué build estaba corriendo el navegador.
 *
 * Sin esto, cuando llega un reporte de error no hay forma de saber si esa
 * pestaña tenía el arreglo que acabábamos de publicar o seguía con el bundle
 * viejo — y esa es justo la pregunta que decide si el arreglo funciona o no.
 * Pasó el 1 ago 2026: dos caídas por chunk vencido, y para saber si el guard de
 * deploy estaba activo hubo que deducirlo de las horas de los commits.
 *
 * Es la ruta del script de entrada, que lleva el hash del build. No es dato
 * personal: identifica la versión del sitio, no a quien la usa.
 */
export function registerBuild(huella: string | null): void {
  if (!API_KEY || !initialized || !huella) return;
  try {
    posthog.register({ app_build: huella });
  } catch {
    // Analytics nunca puede romper el arranque de la app.
  }
}

export function resetAnalytics(): void {
  if (!API_KEY || !initialized) return;
  posthog.reset();
}

/**
 * Eventos canónicos del producto. Mantener esta lista alineada con los KPIs
 * del PRD sección 6 — agregar nuevos solo cuando midan algo accionable.
 */
export type AnalyticsEvent =
  // Salud de la app: lo captura el ErrorBoundary global. Sin esto, una
  // pantalla en blanco en producción no deja ningún rastro.
  | 'app_error'
  // Funnel de adquisición
  | 'signup_started'
  | 'signup_completed'
  | 'pricing_viewed'
  | 'subscription_checkout_started'
  | 'free_plan_cta_clicked' // Freemium: click en la tarjeta Free de /pricing
  | 'subscription_activated'
  // Funnel de reserva 1:1
  | 'mentor_directory_viewed'
  | 'mentor_profile_viewed'
  // Video de presentación del guru abierto desde su ficha (foto o botón).
  | 'guru_intro_video_opened'
  // Curso en vivo: abrió su video y lo compartió (canal y desde dónde).
  | 'live_course_video_opened'
  | 'live_course_shared'
  // Curso en vivo: el inscrito tocó "Entrar" o "Ver la grabación" (mode).
  | 'live_course_joined'
  | 'guru_waitlist_joined' // Visitante se apuntó a la waitlist de un Guru sin calendar
  | 'booking_flow_started'
  | 'booking_payment_succeeded'
  | 'booking_confirmed_premium_credit' // Sprint 10.30: consumió crédito Premium
  | 'booking_coupon_applied' // Sprint G5: cliente aplicó cupón de descuento
  | 'referral_share_clicked' // Sprint G8: usuario clickeó botón de compartir referido
  | 'referral_link_copied'   // Sprint G8: usuario copió su link de referido
  | 'referral_code_applied'  // Sprint G8: nuevo signup aplicó código de referido
  | 'booking_cancelled'
  | 'booking_review_submitted'
  // Funnel de contenido
  | 'course_enrolled'
  | 'lesson_completed'
  | 'course_completed'
  // Funnel de comunidad
  | 'community_post_created'
  | 'community_comment_created'
  | 'event_rsvp'
  // Home público
  | 'home_video_started'     // el video del home arrancó (autoplay al hacer scroll)
  | 'home_video_unmuted'     // la persona activó el sonido del video del home
  | 'home_video_completed'   // lo vio completo con sonido (tarjeta final)
  // Radar (tendencias / opiniones)
  | 'radar_opine_started'      // user abrió el composer desde un tema del Radar
  | 'radar_post_created'       // se creó un post a partir de un tema del Radar
  | 'radar_take_featured'      // una opinión fue destacada
  | 'radar_social_published'   // admin publicó una opinión a redes
  | 'radar_take_reported'      // se reportó una opinión
  | 'radar_poll_voted';        // user votó en una encuesta del Radar
